import React, {useState, useEffect} from "react";
import {
    Container,
    Box,
    Grid,
    Button,
    makeStyles,
    useMediaQuery,
    useTheme,
    Typography,
    Dialog,
    CircularProgress,
    DialogTitle, DialogContent, DialogActions
} from "@material-ui/core";
import Navigation from "../../../Components/Navigation";
import AppointmentsInnerNavigation from "../../../Components/Navigation/AppointmentsInnerNavigation";
import AppointmentCard from "../../../Components/AppointmentCard/AppointmentCard";
import {AnimalProfile, FilterAppointments, MedicalRecordsModal} from "../../../Components";
import API from "../../../helpers/apiHelpers";
import ChatContainer from "../../../Components/ChatContainer";
import {colorInputBorder, colorInputBorderGrayish, colorPrimary} from "../../../Constants/theme";
import {useHistory, useLocation} from "react-router-dom";
import {getAppointmentType, servicesList} from "../../../Constants/serviceType";
import {useSnackbar} from "notistack";
import {RootState, useAppDispatch} from "../../../redux/store";
import {useSelector} from "react-redux";
import moment from "moment";
import RecordListItem from "../../../Components/ListItems/RecordListItem";
import {
    setPastAppts,
    loadMorePastAppts,
    retrieveAppointments,
} from "../../../redux/appointmentList/appointmentList.actions";
import {
    getAppointmentNotes,
    setSelectedAppointment as setAppointmentToRedux
} from "../../../redux/appointment/appointment.actions";
import {SRLWrapper} from "simple-react-lightbox";
import {updateShowVideo} from "../../../redux/images/images.actions";
import ReactWebMediaPlayer from "react-web-media-player";
import InfiniteScroll from "react-infinite-scroll-component";

const useStyle = makeStyles((theme) => ({
    buttons: {
        width: 184,
        marginBottom: 10,
        marginRight: 13,
    },
    filterButton: {
        padding: "18px 47px",
        marginBottom: 10,
        [theme.breakpoints.down("xs")]: {
            marginBottom: 15
        }
    },
    itemDivider: {
        borderBottom: `1px solid ${colorInputBorderGrayish}`
    },
    actions: {
        paddingLeft: "24px"
    },
    dialog: {
        padding: "30px 0 30px 25px",
        width: "900px"
    },
    videoDialog: {
        "& .MuiDialog-paper": {
            overflowY: "hidden",
        },
        "& .MuiDialog-paperFullWidth": {
            width: "unset",
        },
    },
    appointmentsContainer: {
        display: "flex",
        flexDirection: "column",
    },
    list: {
        height: "calc(100vh - 230px)",
        flexGrow: 1,
        overflowY: "auto",
        "&::-webkit-scrollbar": {
            width: 5,
        },
        "&::-webkit-scrollbar-thumb": {
            background: colorInputBorder,
            borderRadius: "6px",
        },
        "&::-webkit-scrollbar-track": {
            boxShadow: "inset 0 0 6px rgba(0, 0, 0, 0.3)",
        },
        [theme.breakpoints.down("sm")]: {
            height: "calc(100vh - 295px)",
        },
    },
}));

const lightBoxOptions = {
    settings: {
        autoplaySpeed: 0,
        downloadedFileName: "image"
    },
    thumbnails: {
        showThumbnails: false
    },
    buttons: {
        showNextButton: false,
        showPrevButton: false,
    }
};

const UpcomingAppointments = (() => {
    const theme = useTheme();
    const history = useHistory();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const classes = useStyle();
    const [openProfileModal, setOpenProfileModal] = useState(false);
    const [openChatDialog, setOpenChatDialog] = useState(false);
    const [vetIds, setVetIds] = useState([]);
    const [vetProfiles, setVetProfiles] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [chatData, setChatData] = useState(null);
    const [openNotesModal, setOpenNotesModal] = useState(false);
    const [openRecordsModal, setOpenRecordsModal] = useState(false);
    const [openFilterModal, setOpenFilterModal] = useState(false);
    const [filtered, setFiltered] = useState(false);
    const [selectedAppointment, setSelectedAppointment] = useState();
    const [selectedRecord, setSelectedRecord] = useState(null);
    const [recordsList, setRecordsList] = useState(null);
    const todayDate = new Date();
    const pathState = useLocation();
    const date = moment(todayDate).format();
    const dispatch = useAppDispatch();
    const imageObj = useSelector((state: RootState) => state?.images.images);
    const user = useSelector((state: RootState) => state?.user);
    const pastAppointments = useSelector((state: RootState) => state?.appointmentList.pastAppointments);
    const showVideo = useSelector((state: RootState) => state?.images.showVideo);
    const {enqueueSnackbar} = useSnackbar();
    const [offset, setOffset] = useState(0);
    const [hasMore, setHasMore] = useState(true);
    const params = {
        appointmentType: servicesList,
        status: ["ACCEPTED", "REQUEST_MADE", "NOT_APPLICABLE", "CANCELLED"],
        offset: offset,
        limit: 20,
        isFinished: true
    };
    const iOS = () => {
        return [
            "iPhone Simulator",
            "iPod Simulator",
            "iPhone",
            "iPod",
        ].includes(navigator.platform)
            // iPad on iOS 13 detection
            || (navigator.userAgent.includes("Mac") && "ontouchend" in document);
    };

    const updatePastAppts = () => {
        const response = offset ? dispatch(loadMorePastAppts(params)) : dispatch(setPastAppts(params));
        return response
            .then(data => {
                setHasMore(data.length > 0);
                setOffset(prev => prev + 20);
                const vetIds = data.map(({vetUserId}) => vetUserId);
                const setVets = new Set(vetIds);
                const vetArrayResult = Array.from(setVets);
                setVetIds(vetArrayResult);
            })
            .catch((error) => {
                console.error(error);
            });

    };

    useEffect(() => {
        if (pathState.state === "past") {
            history.replace({state: undefined});
            enqueueSnackbar("The appointment is no longer active. You can view the appointment’s notes from your past appointments.", {variant: "error"});
        }
        setIsLoading(true);
        updatePastAppts()
            .finally(() => {
                setIsLoading(false);
            });
    }, []);

    useEffect(() => {
        if (openChatDialog) {
            const onShowDialog = () => {
                setTimeout(() => {
                    if (document.querySelector(".MuiDialog-container")) {
                        document.querySelector(".MuiDialog-container").removeAttribute("tabindex");
                        return;
                    } else {
                        onShowDialog();
                    }
                }, 2000);
            };

            onShowDialog();
        }
    }, [openChatDialog]);

    const handleChatData = (open, data, over = false,) => {
        if (open) {
            setOpenChatDialog(true);
            setChatData({data, over});
        } else {
            setOpenChatDialog(false);
            setChatData(null);
        }
    };

    const searchAppointments = (params) => {
        setIsLoading(true);
        dispatch(setPastAppts(params))
            .then(list => {
                setFiltered(true);
                const vetIds = list.map(({vetUserId}) => vetUserId);
                const setVets = new Set(vetIds);
                const vetArrayResult = Array.from(setVets);
                setVetIds(vetArrayResult);
            })
            .catch(() => {
                enqueueSnackbar("An error occurred while trying to search for appointments.", {
                    variant: "error",
                    autoHideDuration: 4000
                });
            }).finally(() => {
                setIsLoading(false);
            });
    };

    const getNotesForAppointment = async (id) => {
        setSelectedAppointment(id);
        setIsLoading(true);
        setOpenRecordsModal(true);
        try {
            const records = await dispatch(getAppointmentNotes(
                user.role === "OWNER" ?
                    {appointmentId: id} : {animalId: id}
            ));
            setRecordsList(records);
        } catch (err) {
            //
        } finally {
            setIsLoading(false);
        }
    };

    const handleCloseTranscript = () => {
        setOpenChatDialog(false);
        setChatData(null);
    };

    useEffect(() => {
        if (vetIds.length !== 0) {
            const vetProfilePromises = vetIds.map(item => API.getProfiles({userId: item}));
            Promise.all(vetProfilePromises)
                .then(values => {
                    let vetProfileArray = [];
                    values.forEach(item => vetProfileArray = [...vetProfileArray, ...item]);
                    setVetProfiles(vetProfileArray);
                })
                .catch(console.error)
                .finally(() => {
                    setIsLoading(false);
                });
        }
    }, [vetIds]);

    return (
        <Box>
            <Container maxWidth="md">
                <Box marginTop={isMobile ? "50px" : "100px"}>
                    <Grid container>
                        {!isMobile &&
                        <Grid item md={3} xs={12}>
                            <Navigation/>
                        </Grid>
                        }
                        <Grid item md={9} xs={12} className={classes.appointmentsContainer}>
                            <Box display="flex" flexWrap="wrap" justifyContent="space-between">
                                <AppointmentsInnerNavigation/>
                                {user.role !== "OWNER" &&
                                <Button
                                    variant="outlined"
                                    color="secondary"
                                    className={classes.filterButton}
                                    onClick={() => setOpenFilterModal(true)}
                                >
                                    Search
                                </Button>
                                }
                            </Box>
                            <Box
                                id="scrollableDiv"
                                className={classes.list}
                                style={{height: user.role == "OWNER" ? iOS() ? "calc(100vh - 295px)" : isMobile ? "calc(100vh - 240px)" : "" : "",}}
                            >
                                {
                                    isLoading ?
                                        <Box width="100%" height="300px" display="flex" justifyContent="center"
                                            alignItems="center">
                                            <CircularProgress/>
                                        </Box>
                                        :
                                        !filtered ?
                                            (!!pastAppointments?.length ?
                                                <InfiniteScroll
                                                    dataLength={pastAppointments.length} //This is important field to render the next data
                                                    next={updatePastAppts}
                                                    hasMore={hasMore}
                                                    loader={
                                                        <Box width="100%" height="300px" display="flex"
                                                            justifyContent="center"
                                                            alignItems="center">
                                                            <CircularProgress/>
                                                        </Box>
                                                    }
                                                    scrollableTarget="scrollableDiv"
                                                >
                                                    {pastAppointments.map(item => {
                                                        const {vetUserId, id} = item;
                                                        const vetProfile = vetProfiles.find((vet) => vet.userId === vetUserId);
                                                        return (
                                                            <Box key={id}>
                                                                <AppointmentCard
                                                                    cardData={{
                                                                        appointmentData: item,
                                                                        vetProfile,
                                                                    }}
                                                                    showNotes={getNotesForAppointment}
                                                                    handleChatData={handleChatData}
                                                                    over={true}
                                                                />
                                                                <Grid container>
                                                                    {user.role !== "OWNER" &&
                                                                        <>
                                                                            <Button
                                                                                className={classes.buttons}
                                                                                variant="contained"
                                                                                style={{color: colorPrimary}}
                                                                                onClick={() => {
                                                                                    dispatch(setAppointmentToRedux(item));
                                                                                    history.push({
                                                                                        pathname: "/apointmentPrescriptions",
                                                                                        state: {
                                                                                            appointmentId: id,
                                                                                            redirectBack: true
                                                                                        }
                                                                                    });
                                                                                }}
                                                                                disableElevation
                                                                            >
                                                                                Prescription / OTC
                                                                            </Button>
                                                                            <Button
                                                                                className={classes.buttons}
                                                                                variant="contained"
                                                                                style={{color: colorPrimary}}
                                                                                onClick={() => {
                                                                                    dispatch(setAppointmentToRedux(item));
                                                                                    history.push({
                                                                                        pathname: "/apointmentNotes",
                                                                                        state: {
                                                                                            appointmentId: id,
                                                                                            afterAppt: false
                                                                                        }
                                                                                    });
                                                                                }}
                                                                                disableElevation
                                                                            >
                                                                                Notes
                                                                            </Button>
                                                                        </>
                                                                    }
                                                                </Grid>
                                                            </Box>
                                                        );
                                                    })}
                                                </InfiniteScroll>
                                                :
                                                <Typography>No past appointments</Typography>
                                            )
                                            :
                                            (pastAppointments.length !== 0 ?
                                                (pastAppointments.map(item => {
                                                    const {vetUserId, id, type, chatId} = item;
                                                    const vetProfile = vetProfiles.find((vet) => vet.userId === vetUserId);
                                                    return (
                                                        <Box key={id}>
                                                            <AppointmentCard
                                                                cardData={{
                                                                    appointmentData: item,
                                                                    vetProfile,
                                                                }}
                                                                showNotes={getNotesForAppointment}
                                                                handleChatData={handleChatData}
                                                                over={true}
                                                            />
                                                            <Grid container>
                                                                {user.role !== "OWNER" &&
                                                                <>
                                                                    <Button
                                                                        className={classes.buttons}
                                                                        variant="contained"
                                                                        style={{color: colorPrimary}}
                                                                        onClick={() => {
                                                                            dispatch(setAppointmentToRedux(item));
                                                                            history.push({
                                                                                pathname: "/apointmentPrescriptions",
                                                                                state: {
                                                                                    appointmentId: id,
                                                                                    redirectBack: true
                                                                                }
                                                                            });
                                                                        }}
                                                                        disableElevation
                                                                    >
                                                                        Prescription / OTC
                                                                    </Button>
                                                                    <Button
                                                                        className={classes.buttons}
                                                                        variant="contained"
                                                                        style={{color: colorPrimary}}
                                                                        onClick={() => {
                                                                            dispatch(setAppointmentToRedux(item));
                                                                            history.push({
                                                                                pathname: "/apointmentNotes",
                                                                                state: {
                                                                                    appointmentId: id,
                                                                                    afterAppt: false
                                                                                }
                                                                            });
                                                                        }}
                                                                        disableElevation
                                                                    >
                                                                        Notes
                                                                    </Button>
                                                                </>
                                                                }
                                                            </Grid>
                                                        </Box>
                                                    );
                                                }))
                                                :
                                                <Box marginTop="20px" maxWidth="90%">
                                                    <Box marginBottom="20px">
                                                        <Typography variant="h4">Sorry, please search
                                                            again!</Typography>
                                                    </Box>
                                                    <Box marginBottom="20px">
                                                        <Typography color="textSecondary">
                                                            Unfortunately there are no past appointments that match the
                                                            filters
                                                            that you selected.
                                                            Please try other search options
                                                        </Typography>
                                                    </Box>
                                                    <Button
                                                        className={classes.buttons}
                                                        variant="contained"
                                                        color="primary"
                                                        disableElevation
                                                        onClick={() => setOpenFilterModal(true)}
                                                    >
                                                        Update Search
                                                    </Button>
                                                </Box>)
                                }
                            </Box>
                        </Grid>
                    </Grid>
                </Box>
            </Container>
            <Dialog fullScreen open={openChatDialog} container={() => document.getElementById("root")}>
                <ChatContainer data={chatData} over={true} handleChatData={handleChatData}
                    handleCloseTranscript={handleCloseTranscript} isTranscript={true}/>
            </Dialog>
            <AnimalProfile isOpen={openProfileModal} setModalProfile={setOpenProfileModal}/>
            <SRLWrapper
                elements={imageObj}
                options={lightBoxOptions}
            />
            <Dialog
                fullWidth={true}
                disableEnforceFocus
                className={classes.videoDialog}
                onClose={() => dispatch(updateShowVideo(false))}
                open={showVideo}
            >
                <ReactWebMediaPlayer
                    style={{marginBottom: 0,}}
                    video={imageObj[0].src}
                />
            </Dialog>
            <Dialog
                open={openRecordsModal}
                onClose={() => {
                    setOpenRecordsModal(false);
                    setRecordsList(null);
                }}
                classes={{paper: classes.dialog}}
                scroll="body"
                disableEnforceFocus
            >
                <DialogTitle color="primary">
                    <Typography variant="h3">
                        Medical Records
                    </Typography>
                </DialogTitle>
                <DialogContent>
                    {isLoading ?
                        <Box width="100%" height="300px" display="flex" justifyContent="center" alignItems="center">
                            <CircularProgress/>
                        </Box>
                        : (recordsList?.length) ?
                            [...recordsList]
                                .sort((a, b) => moment(b.created).diff(moment(a.created)))
                                .map(({id, created, appointment}, idx) => {
                                    const vet = vetProfiles.find(profile => profile.userId === appointment?.vetUserId);
                                    const title = appointment.status === "NOT_APPLICABLE" ? "In-Clinic Appointment" : `${getAppointmentType(appointment?.type)} ${vet?.lastName? (`- Dr. ${vet.lastName}`) : ""}`;
                                    const noteCreated = moment(created).format("MMMM DD, YYYY");
                                    return <RecordListItem
                                        key={idx}
                                        id={id}
                                        title={title}
                                        date={noteCreated}
                                        setSelectedRecord={setSelectedRecord}
                                        setOpenModal={setOpenNotesModal}
                                        styles={(idx === recordsList.length - 1) ? "" : classes.itemDivider}
                                    />;
                                })
                            :
                            <Typography variant="h6">No medical records</Typography>}
                </DialogContent>
                <DialogActions classes={{root: classes.actions}}
                >
                    <Box display="flex" justifyContent="flex-start" width="100%">
                        <Button variant="contained"
                            onClick={() => {
                                setOpenRecordsModal(false);
                                setRecordsList(null);
                            }}>
                            Close
                        </Button>
                    </Box>
                </DialogActions>
            </Dialog>
            <MedicalRecordsModal isOpen={openNotesModal} setOpenDialog={setOpenNotesModal} noteId={selectedRecord}/>
            <FilterAppointments isOpen={openFilterModal} setOpenDialog={setOpenFilterModal}
                handleChange={searchAppointments} offset={offset} setOffset={setOffset}/>
        </Box>
    );
});

export default UpcomingAppointments;
